import { type JSX,useCallback, useRef, useState } from 'react';

import { Box, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import { useDrawerContext } from 'common/components/DrawerContext/drawerContext';
import { EditDrawer } from 'common/components/EditDrawer';
import { setSaveButtonEnabled } from 'common/components/EditDrawer/editDrawerSlice';
import { DEFAULT_ERROR_HANDLER_INLINE_STYLES } from 'common/components/errorHandler/ErrorHandler';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { ReadOnlyTextField } from 'common/components/sharedCustomComponents/ReadOnlyTextField/ReadOnlyTextField';
import {
    AclAccessPostProperties,
    APIAclAccessPostParams,
    APIAclAccessPutParams
} from 'common/services/api/Acls/AclAccesses.api';
import {
    postAclAccess,
    PostAclAccessThunkResponse,
    putAclAccess,
    PutAclAccessThunkResponse
} from 'common/services/api/Acls/AclAccesses.thunk';
import { EditDrawerContentProps, Operations } from 'common/types/TypeDefs';
import { editDrawerTextFieldStyles } from 'common/utils/StyleHelpers';

const ACL_ACCESS_VALUE_PLACEHOLDER = '{\n  "permissions": []\n}';

interface AclAccessItem {
    name: string;
    properties: {
        permissions?: string[]
    }
}

interface AclAccessDrawerProperties {
    aclAccessNameFieldRef: React.RefObject<HTMLInputElement | null>;
    aclAccessValueFieldRef: React.RefObject<HTMLInputElement | null>;
    tenantName: string;
    tenantId: string;
}

function useAclAccessDrawer(
    entityName: string,
    drawerProps: AclAccessDrawerProperties,
    editingItemInfo?: EditDrawerContentProps<AclAccessItem>['editingItemInfo']
) {
    const dispatch = useAppDispatch();
    const [isValueWithError, setIsValueWithError] = useState<boolean>(false);

    const drawerContext = useDrawerContext<
        AclAccessItem,
        AclAccessDrawerProperties,
        APIAclAccessPostParams,
        PostAclAccessThunkResponse,
        APIAclAccessPutParams,
        PutAclAccessThunkResponse
    >(entityName, drawerProps, editingItemInfo);

    const handleSaveBtnEnabled = useCallback(() => {
        dispatch(setSaveButtonEnabled(
            !!drawerProps.aclAccessNameFieldRef?.current?.value &&
            !!drawerProps.aclAccessValueFieldRef?.current?.value));
    }, [dispatch, drawerProps.aclAccessNameFieldRef, drawerProps.aclAccessValueFieldRef]);

    const parseAccessValueField = useCallback(() => {
        const aclAccessValue = drawerProps.aclAccessValueFieldRef.current?.value;
        if (!aclAccessValue) {
            return undefined;
        }
        let parsedValue;
        try {
            parsedValue = JSON.parse(aclAccessValue) as Record<string, unknown>;
        } catch {
            return undefined;
        }
        if (
            !Array.isArray(parsedValue?.permissions) ||
            !parsedValue.permissions.length
        ) {
            return undefined;
        }
        return parsedValue as AclAccessPostProperties;
    }, [drawerProps.aclAccessValueFieldRef]);

    const getPostOptions = useCallback((): APIAclAccessPostParams | null => {
        const properties = parseAccessValueField();
        if (!properties) {
            setIsValueWithError(true);
            drawerContext.setProcessing(false);
            return null;
        }
        setIsValueWithError(false);
        return {
            tenantId: drawerProps.tenantId,
            name: drawerProps.aclAccessNameFieldRef.current?.value ?? '',
            properties
        };
    }, [parseAccessValueField, drawerProps, drawerContext]);

    const getPutOptions = useCallback((): APIAclAccessPutParams | null => {
        if (!editingItemInfo?.editingItemId) {
            return null;
        }
        const properties = parseAccessValueField();
        if (!properties) {
            setIsValueWithError(true);
            drawerContext.setProcessing(false);
            return null;
        }
        setIsValueWithError(false);
        return {
            id: editingItemInfo?.editingItemId?.toString() ?? '',
            tenantId: drawerProps.tenantId,
            name: drawerProps.aclAccessNameFieldRef.current?.value ?? '',
            properties,
        };
    }, [parseAccessValueField, editingItemInfo?.editingItemId, drawerProps, drawerContext]);

    const createSuccessHandler = useCallback((postResult: PostAclAccessThunkResponse) => {
        drawerContext.createSuccessHandler(postResult);
        dispatch(setRows(postResult.rows));
    }, [drawerContext, dispatch]);

    const editSuccessHandler = useCallback((putResult: PutAclAccessThunkResponse) => {
        drawerContext.editSuccessHandler(putResult);
        dispatch(setRows(putResult.rows));
    }, [drawerContext, dispatch]);

    const postCallback = useCallback(() => {
        drawerContext.postCallback(postAclAccess, getPostOptions, createSuccessHandler);
    }, [drawerContext, getPostOptions, createSuccessHandler]);

    const putCallback = useCallback(() => {
        drawerContext.putCallback(putAclAccess, getPutOptions, editSuccessHandler);
    }, [drawerContext, getPutOptions, editSuccessHandler]);

    return {
        ...drawerContext,
        isValueWithError,
        handleSaveBtnEnabled,
        postCallback,
        putCallback
    };
}

function CreateContent({
    entityName,
    processing,
    nameInConflict,
    isValueWithError,
    handleSaveBtnEnabled,
    postCallback,
    drawerProps
}: Readonly<{
    entityName: string;
    processing: boolean;
    nameInConflict: boolean;
    isValueWithError: boolean;
    handleSaveBtnEnabled: () => void;
    postCallback: () => void;
    drawerProps: AclAccessDrawerProperties;
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={postCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <TextField
                    sx={editDrawerTextFieldStyles(nameInConflict)}
                    inputRef={drawerProps.aclAccessNameFieldRef}
                    size="small"
                    label="ACL Access Name"
                    placeholder="Type ACL Access Name"
                    variant="filled"
                    autoFocus={true}
                    onChange={handleSaveBtnEnabled}
                    InputProps={{
                        disableUnderline: nameInConflict
                    }}
                />
                {nameInConflict &&
                    <Box component='div'
                        display='inline'
                        sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}>
                    ACL Access name already in use
                    </Box>
                }
                <Tooltip
                    placement='left-start'
                    title={
                        <Typography
                            fontSize='small'
                            style={{ whiteSpace: 'pre-line' }}
                        >
                            {
                                'ACL Access Value must be a JSON string with a single field called "permissions" ' +
                                'which is an array of strings that represent ACL Access permissions.\n Example:\n'
                            }
                            <code style={{ whiteSpace: 'pre' }}>
                                {'{\n  "permissions": [\n    "GET",\n    "POST"\n  ]\n}'}
                            </code>
                        </Typography>
                    }
                >
                    <TextField
                        multiline={true}
                        minRows={3}
                        sx={editDrawerTextFieldStyles(isValueWithError)}
                        inputRef={drawerProps.aclAccessValueFieldRef}
                        size="small"
                        label="ACL Access Value"
                        defaultValue={ACL_ACCESS_VALUE_PLACEHOLDER}
                        placeholder={ACL_ACCESS_VALUE_PLACEHOLDER}
                        variant="filled"
                        onChange={handleSaveBtnEnabled}
                    />
                </Tooltip>
                {isValueWithError &&
                    <Box component='div'
                        display='inline'
                        sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}>
                    Invalid ACL Access Value format. For format specifications hover over the ACL Access Value field.
                    </Box>
                }
                <ReadOnlyTextField label="Tenant Name" value={drawerProps.tenantName} />
            </Stack>
        </EditDrawer>
    );
}

function EditContent({
    entityName,
    processing,
    nameInConflict,
    isValueWithError,
    handleSaveBtnEnabled,
    putCallback,
    drawerProps,
    editingItemInfo
}: Readonly<{
    entityName: string;
    processing: boolean;
    nameInConflict: boolean;
    isValueWithError: boolean;
    handleSaveBtnEnabled: () => void;
    putCallback: () => void;
    drawerProps: AclAccessDrawerProperties;
    editingItemInfo?: EditDrawerContentProps<AclAccessItem>['editingItemInfo'];
}>): JSX.Element {
    const { name, properties } = editingItemInfo?.editingRow ?? {};
    const permissionsMaper = (el: string) => `\n    "${el}"`;
    const value = `{\n  "permissions": [${properties?.permissions?.map(permissionsMaper).join(',') ?? '\n    '}\n  ]\n}`;

    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={putCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <TextField
                    sx={editDrawerTextFieldStyles(nameInConflict)}
                    inputRef={drawerProps.aclAccessNameFieldRef}
                    size="small"
                    label="ACL Access Name"
                    placeholder="Type ACL Access Name"
                    defaultValue={name}
                    variant="filled"
                    autoFocus={true}
                    onChange={handleSaveBtnEnabled}
                    InputProps={{
                        disableUnderline: nameInConflict
                    }}
                />
                {nameInConflict &&
                    <Box component='div'
                        display='inline'
                        sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}>
                    ACL Access name already in use
                    </Box>
                }
                <Tooltip
                    placement='left-start'
                    title={
                        <Typography
                            fontSize='small'
                            style={{ whiteSpace: 'pre-line' }}
                        >
                            {
                                'ACL Access Value must be a JSON string with a single field called "permissions" ' +
                                'which is an array of strings that represent ACL Access permissions.\n Example:\n'
                            }
                            <code style={{ whiteSpace: 'pre' }}>
                                {'{\n  "permissions": [\n    "GET",\n    "POST"\n  ]\n}'}
                            </code>
                        </Typography>
                    }
                >
                    <TextField
                        multiline={true}
                        minRows={3}
                        sx={editDrawerTextFieldStyles(isValueWithError)}
                        inputRef={drawerProps.aclAccessValueFieldRef}
                        size="small"
                        label="ACL Access Value"
                        defaultValue={value}
                        placeholder={ACL_ACCESS_VALUE_PLACEHOLDER}
                        variant="filled"
                        onChange={handleSaveBtnEnabled}
                    />
                </Tooltip>
                {isValueWithError &&
                    <Box component='div'
                        display='inline'
                        sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}>
                    Invalid ACL Access Value format. For format specifications hover over the ACL Access Value field.
                    </Box>
                }
                <ReadOnlyTextField label="Tenant Name" value={drawerProps.tenantName} />
            </Stack>
        </EditDrawer>
    );
}

export function AclAccessesEditContent({
    title,
    editingItemInfo,
}: Readonly<EditDrawerContentProps<AclAccessItem>>) {
    const tenantDetails = useAppSelector(selectPageInfo);
    const aclAccessDrawerProps: AclAccessDrawerProperties = {
        aclAccessNameFieldRef: useRef<HTMLInputElement>(null),
        aclAccessValueFieldRef: useRef<HTMLInputElement>(null),
        tenantId: tenantDetails?.id ?? '',
        tenantName: tenantDetails?.name ?? ''
    };

    const drawerState = useAclAccessDrawer(title, aclAccessDrawerProps, editingItemInfo);

    if (drawerState.action === Operations.Create || drawerState.action === Operations.Link) {
        return (
            <CreateContent
                entityName={title}
                processing={drawerState.processing}
                nameInConflict={drawerState.nameInConflict}
                isValueWithError={drawerState.isValueWithError}
                handleSaveBtnEnabled={drawerState.handleSaveBtnEnabled}
                postCallback={drawerState.postCallback}
                drawerProps={aclAccessDrawerProps}
            />
        );
    } else if (drawerState.action === Operations.Edit) {
        if (!editingItemInfo) {
            return <></>;
        }
        return (
            <EditContent
                entityName={title}
                processing={drawerState.processing}
                nameInConflict={drawerState.nameInConflict}
                isValueWithError={drawerState.isValueWithError}
                handleSaveBtnEnabled={drawerState.handleSaveBtnEnabled}
                putCallback={drawerState.putCallback}
                drawerProps={aclAccessDrawerProps}
                editingItemInfo={editingItemInfo}
            />
        );
    }

    return <></>;
}
